import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Check } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { STRIPE_PLANS, type BillingPeriod, type PlanType } from "@/lib/stripe-plans";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
const plans: Array<{
  id: PlanType;
  name: string;
  description: string;
  messageLimit: string;
  messageExtra: string;
  features: string[];
  popular?: boolean;
}> = [{
  id: "inicial",
  name: "Essencial",
  description: "Perfeito para começar a automatizar",
  messageLimit: "2.000",
  messageExtra: "0,08",
  features: ["1 usuário", "1 agente de IA", "3.000 leads", "2.000 mensagens/mês incluídas", "R$ 0,08 por mensagem extra", "1 conexão (WhatsApp ou Instagram)", "Suporte por email"]
}, {
  id: "intermediario",
  name: "Plus",
  description: "Para times que querem escalar",
  messageLimit: "10.000",
  messageExtra: "0,06",
  features: ["5 usuários", "3 agentes de IA", "20.000 leads", "10.000 mensagens/mês incluídas", "R$ 0,06 por mensagem extra", "Até 3 conexões (WhatsApp + Instagram)", "2 canais disponíveis"],
  popular: true
}, {
  id: "pro",
  name: "PRO",
  description: "Para operações de alto volume",
  messageLimit: "40.000",
  messageExtra: "0,05",
  features: ["10 usuários", "6 agentes de IA", "100.000 leads", "40.000 mensagens/mês incluídas", "R$ 0,05 por mensagem extra", "Até 6 conexões (WhatsApp + Instagram)", "2 canais disponíveis"]
}];
const formatPrice = (price: number): string => {
  return price.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};
const ANNUAL_DISCOUNT_PERCENT = 10;
export const Pricing = () => {
  const [loading, setLoading] = useState<string | null>(null);
  const [billingPeriodByPlan, setBillingPeriodByPlan] = useState<Record<PlanType, BillingPeriod>>({
    inicial: "yearly",
    intermediario: "yearly",
    pro: "yearly"
  });
  const {
    toast
  } = useToast();
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const handleCheckout = async (planId: PlanType, billingPeriod: BillingPeriod) => {
    setLoading(planId);
    const plan = STRIPE_PLANS[planId];
    const priceId = billingPeriod === "monthly" ? plan.monthly.priceId : plan.yearly.priceId;

    // Check if user is logged in
    if (!user) {
      toast({
        title: "Cadastro necessário",
        description: "Crie sua conta para continuar com o plano selecionado."
      });
      setTimeout(() => {
        navigate(`/auth?plan=${planId}&priceId=${priceId}&period=${billingPeriod}`);
      }, 1000);
      setLoading(null);
      return;
    }

    // User is logged in, proceed with checkout
    try {
      const email = user.email;
      if (!email) {
        toast({
          title: "Erro",
          description: "Email do usuário não encontrado.",
          variant: "destructive"
        });
        setLoading(null);
        return;
      }
      console.log("[Pricing] Calling create-checkout with:", {
        priceId,
        email,
        billingPeriod
      });
      const {
        data,
        error
      } = await supabase.functions.invoke("create-checkout", {
        body: {
          priceId,
          email
        }
      });
      console.log("[Pricing] Response:", {
        data,
        error
      });
      if (error) {
        console.error("[Pricing] Error:", error);
        throw error;
      }
      if (data?.url) {
        console.log("[Pricing] Opening checkout URL:", data.url);
        const newWindow = window.open(data.url, "_blank");
        if (!newWindow) {
          toast({
            title: "Popup bloqueado",
            description: "Permita popups para continuar com o pagamento.",
            variant: "destructive"
          });
          setTimeout(() => {
            window.location.href = data.url;
          }, 2000);
        } else {
          toast({
            title: "Redirecionando",
            description: "Você será redirecionado para o pagamento."
          });
        }
      } else {
        throw new Error("Nenhuma URL de checkout retornada");
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast({
        title: "Erro",
        description: error instanceof Error ? error.message : "Não foi possível iniciar o checkout. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(null);
    }
  };
  const getPrice = (planId: PlanType, billingPeriod: BillingPeriod): string => {
    const plan = STRIPE_PLANS[planId];
    if (billingPeriod === "monthly") {
      return formatPrice(plan.monthly.price);
    }
    return formatPrice(plan.yearly.pricePerMonth);
  };
  return <section id="pricing" className="py-24 bg-background border-t border-border/40 scroll-mt-24">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Planos transparentes para cada fase do seu negócio
          </h2>
          <p className="text-xl text-muted-foreground">
            Contrate agora e escale conforme cresce
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => {
          const billingPeriod = billingPeriodByPlan[plan.id] ?? "yearly";
          return <Card key={index} className={`relative overflow-visible ${plan.popular ? "border-2 border-primary shadow-xl scale-105" : "border-border"}`}>
              {plan.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-semibold">
                    Mais Popular
                  </span>
                </div>}

              <CardHeader className="text-center pb-8 pt-8 overflow-visible">
                <CardTitle className="text-2xl mb-2">{plan.name}</CardTitle>
                <CardDescription className="text-base mb-4">
                  {plan.description}
                </CardDescription>
                <div className="mt-4 flex flex-nowrap justify-center items-baseline gap-1 overflow-visible text-center">
                  <span className="text-2xl sm:text-3xl md:text-4xl font-bold tabular-nums shrink-0">R$</span>
                  <span className="text-2xl sm:text-3xl md:text-4xl font-bold tabular-nums shrink-0">
                    {getPrice(plan.id, billingPeriod)}
                  </span>
                  <span className="text-muted-foreground text-lg sm:text-xl md:text-2xl shrink-0">/mês</span>
                </div>

                <div className="mt-4 flex items-center justify-center gap-2">
                  <span className={cn("text-xs font-medium transition-colors", billingPeriod === "monthly" ? "text-foreground" : "text-muted-foreground")}>
                    Mensal
                  </span>
                  <Switch checked={billingPeriod === "yearly"} onCheckedChange={checked => setBillingPeriodByPlan(prev => ({
                  ...prev,
                  [plan.id]: checked ? "yearly" : "monthly"
                }))} aria-label={`Alternar para ${billingPeriod === "yearly" ? "mensal" : "anual"}`} />
                  <span className={cn("text-xs font-medium transition-colors", billingPeriod === "yearly" ? "text-foreground" : "text-muted-foreground")}>
                    Anual
                  </span>
                  <span className={cn("bg-green-500/10 text-green-700 dark:text-green-400 text-xs font-semibold px-2 py-0.5 rounded-full transition-opacity", billingPeriod === "yearly" ? "opacity-100" : "opacity-60")}>
                    Economize {ANNUAL_DISCOUNT_PERCENT}%
                  </span>
                </div>

                {billingPeriod === "yearly" && <div className="mt-2 space-y-1">
                    <p className="text-xs text-muted-foreground">
                      12 parcelas mensais • compromisso de 12 meses
                    </p>
                    
                  </div>}
                <div className="mt-2 text-sm text-muted-foreground">
                  <p className="font-medium text-foreground">Até {plan.messageLimit} mensagens/mês</p>
                  <p>R$ {plan.messageExtra} por mensagem adicional</p>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {plan.features.map((feature, featureIndex) => <div key={featureIndex} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-sm text-foreground">{feature}</span>
                  </div>)}
              </CardContent>

              <CardFooter className="pt-6">
                <Button className="w-full" variant={plan.popular ? "hero" : "outline"} size="lg" onClick={() => handleCheckout(plan.id, billingPeriod)} disabled={loading === plan.id}>
                  {loading === plan.id ? "Processando..." : "Começar agora"}
                </Button>
              </CardFooter>
            </Card>;
        })}
        </div>

        <div className="text-center mt-12">
          <p className="text-muted-foreground">
            Precisa de mais? <a href="https://wa.me/5551980577709" target="_blank" rel="noreferrer" className="text-primary hover:underline font-semibold">Entre em contato</a> para um plano Enterprise personalizado
          </p>
        </div>
      </div>
    </section>;
};